import { ITooltipParams, ValueFormatterParams } from "@ag-grid-community/core";
import { isValid } from "date-fns";
import { get, isNil } from "lodash-es";
import { defineMessages } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { Route } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { IHumanReadableFilterConfig } from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";

import i18nService from "@app/config/I18nService";
import DateTimeField from "@app/util/fieldModel/impl/DateTimeField";
import { booleanFormatter, meterDistanceValueFormatter } from "@app/util/fieldModel/impl/fieldUtil";
import TeamField from "@app/util/fieldModel/impl/TeamField";
import UserField from "@app/util/fieldModel/impl/UserField";
import { DURATION_FILTER_OPERATORS, getDurationFilter } from "@app/util/filters/DurationFilter";
import { getDurationRangeFilter } from "@app/util/filters/DurationRangeFilter";
import EmptyFilter from "@app/util/filters/EmptyFilter";
import {
  csvDateTimeFormatter,
  csvNumberFormatter,
  formatDate,
  formatDuration,
  formatRawDate,
  formatRawDateRelative,
} from "@app/util/formatters";
import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";
import getRouteProgress from "@app/util/route/getRouteProgress";
import getRouteStatus, { RouteStatus } from "@app/util/route/getRouteStatus";
import getRouteStatusDisplayName from "@app/util/route/getRouteStatusDisplayName";

import CommonFieldName from "./CommonFieldName";
import FieldModel from "./impl/FieldModel";
import { createField } from "./impl/fieldModelUtil";
import NameField from "./impl/NameField";

export const STOP_FIELD_PREFIX = "stopTime";

const RELATIVE_DATE_FALLBACK_FORMAT = "PPp";
const FULL_DATE_TOOLTIP_FORMAT = "PPPp";

export enum RouteFieldName {
  NAME = CommonFieldName.NAME,
  COUNT = "items",
  STATUS = "status",
  PROGRESS = "progress",
  ACTIONS = "actions",
  START_ADDRESS = "routeDetail.startAddress",
  TOTAL_DISTANCE = "routeDetail.totalDistance",
  TOTAL_DURATION = "routeDetail.totalDuration",
  USER = CommonFieldName.USER,
  START_AT = "routeDetail.startAt",
  CREATED_AT = CommonFieldName.CREATED_AT,
  TEAM = CommonFieldName.TEAM,
  UPDATED_AT = CommonFieldName.UPDATED_AT,
  QUICK_FILTER = "quickFilter",
  STOP_CHECKED_OFF = `${STOP_FIELD_PREFIX}.checkedOff`,
  STOP_ALLOTTED_TIME = `${STOP_FIELD_PREFIX}.allottedTime`,
  STOP_DISPLAY_ORDER = `${STOP_FIELD_PREFIX}.displayOrder`,
  STOP_SCHEDULED_TIME = `${STOP_FIELD_PREFIX}.scheduledTime`,
}

export const displayNames = defineMessages<RouteFieldName>({
  [RouteFieldName.ACTIONS]: {
    id: "route.field.actions",
    defaultMessage: "Actions",
    description: "Header of the row-menu column of route",
  },
  [RouteFieldName.COUNT]: {
    id: "route.field.count",
    defaultMessage: "Stops",
    description: "Items field of route",
  },
  [RouteFieldName.CREATED_AT]: {
    id: "route.field.createdAt",
    defaultMessage: "Date Created",
    description: "Date Created field of route",
  },
  [RouteFieldName.NAME]: {
    id: "route.field.name",
    defaultMessage: "Route Name",
    description: "Name field of route",
  },
  [RouteFieldName.PROGRESS]: {
    id: "route.field.progress",
    defaultMessage: "Progress",
    description: "Progress field of route, the share of stops which are checked off",
  },
  [RouteFieldName.QUICK_FILTER]: {
    id: "route.field.quickFilter",
    defaultMessage: "",
    description: "Quick filter field of route",
  },
  [RouteFieldName.START_ADDRESS]: {
    id: "route.field.routeDetail.startAddress",
    defaultMessage: "Start Address",
    description: "Start Address field of route",
  },
  [RouteFieldName.START_AT]: {
    id: "route.field.startAt",
    defaultMessage: "Start Date",
    description: "Start Date field of route",
  },
  [RouteFieldName.STATUS]: {
    id: "route.field.status",
    defaultMessage: "Status",
    description: "Status field of route, derived from how many stops are checked off",
  },
  [RouteFieldName.STOP_ALLOTTED_TIME]: {
    id: "route.field.stop.allottedTime",
    defaultMessage: "Allotted Time",
    description: "Allotted Time field of route's stop",
  },
  [RouteFieldName.STOP_CHECKED_OFF]: {
    id: "route.field.stop.checkedOff",
    defaultMessage: "Checked Off",
    description: "Checked Off field of route's stop",
  },
  [RouteFieldName.STOP_DISPLAY_ORDER]: {
    id: "route.field.stop.displayOrder",
    defaultMessage: "Display Order",
    description: "Display order field of route's stop",
  },
  [RouteFieldName.STOP_SCHEDULED_TIME]: {
    id: "route.field.stop.scheduledTime",
    defaultMessage: "Scheduled Time",
    description: "Scheduled Time field of route's stop",
  },
  [RouteFieldName.TEAM]: {
    id: "route.field.team",
    defaultMessage: "Team",
    description: "Team field of route",
  },
  [RouteFieldName.TOTAL_DISTANCE]: {
    id: "route.field.totalDistance",
    defaultMessage: "Distance",
    description: "Total distance field of route",
  },
  [RouteFieldName.TOTAL_DURATION]: {
    id: "route.field.totalDuration",
    defaultMessage: "Duration",
    description: "Total duration field of route",
  },
  [RouteFieldName.UPDATED_AT]: {
    id: "route.field.updatedAt",
    defaultMessage: "Date Last Modified",
    description: "Date Modified field of route",
  },
  [RouteFieldName.USER]: {
    id: "route.field.user",
    defaultMessage: "Owner",
    description: "User field of route",
  },
});

const routeFieldModel = new FieldModel(
  [
    new NameField({
      displayName: displayNames[RouteFieldName.NAME],
      displayOrder: 1,
      name: RouteFieldName.NAME,
    }),
    {
      displayName: displayNames[RouteFieldName.COUNT],
      displayOrder: 2,
      features: [FieldFeature.SORTABLE, FieldFeature.VISIBLE_BY_DEFAULT],
      filterType: FieldType.NUMBER,
      name: RouteFieldName.COUNT,
      type: FieldType.NUMBER,
    },
    {
      displayName: displayNames[RouteFieldName.START_ADDRESS],
      features: [],
      filterType: FieldType.STRING,
      name: RouteFieldName.START_ADDRESS,
      type: FieldType.STRING,
    },
    {
      csvExportProperties: { valueFormatter: csvNumberFormatter },
      displayName: displayNames[RouteFieldName.TOTAL_DISTANCE],
      displayOrder: 3,
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.VISIBLE_BY_DEFAULT,
      ],
      filterType: FieldType.NUMBER,
      name: RouteFieldName.TOTAL_DISTANCE,
      type: FieldType.NUMBER,
      valueFormatter: (entity: unknown, value: unknown) =>
        isNotEmpty(value) ? meterDistanceValueFormatter(entity, value) : "—",
    },
    {
      csvExportProperties: { valueFormatter: csvNumberFormatter },
      customFilterConfig: {
        defaultInstance: "duration",
        filterInstances: {
          duration: getDurationFilter({ inSeconds: true }),
          durationRange: getDurationRangeFilter({ inSeconds: true }),
          empty: EmptyFilter,
        },
        operators: [
          ...DURATION_FILTER_OPERATORS,
          { instance: "durationRange", operator: FilterOperator.IN_RANGE },
          { instance: "empty", operator: FilterOperator.EMPTY },
        ],
      } as IHumanReadableFilterConfig,
      displayName: displayNames[RouteFieldName.TOTAL_DURATION],
      displayOrder: 4,
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.VISIBLE_BY_DEFAULT,
      ],
      filterType: FieldType.NUMBER,
      name: RouteFieldName.TOTAL_DURATION,
      type: FieldType.NUMBER,
      valueFormatter: (entity: unknown, value: unknown) => {
        if (!isNotEmpty(value)) {
          return "—";
        }
        const intl = i18nService.getIntl();
        const minutes = Math.round((value as number) / 60);
        return intl ? formatDuration(intl, minutes) : `${minutes} min`;
      },
    },
    new UserField({
      displayName: displayNames[RouteFieldName.USER],
      displayOrder: 5,
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.OWNER_FIELD,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.SORTABLE,
        FieldFeature.VISIBLE_BY_DEFAULT,
      ],
      name: RouteFieldName.USER,
      platformFilterNames: ["userId", "user.id"],
      platformName: "userId",
    }),
    // filter-only field, used to scope a manager's dashboard drill-down:
    new TeamField({
      displayName: displayNames[RouteFieldName.TEAM],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.TEAM_FIELD,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
      ],
      name: RouteFieldName.TEAM,
      platformFilterNames: ["teamId", "team.id"],
    }),
    new DateTimeField({
      // grid-only: the list shows a relative date and the full date on hover. The
      // field keeps its absolute format everywhere else.
      customGridProperties: {
        tooltipValueGetter: ({ data }: ITooltipParams) =>
          formatRawDate((data as Route)?.routeDetail?.startAt, FULL_DATE_TOOLTIP_FORMAT),
        valueFormatter: ({ data }: ValueFormatterParams) =>
          formatRawDateRelative(
            (data as Route)?.routeDetail?.startAt,
            Date.now(),
            "",
            RELATIVE_DATE_FALLBACK_FORMAT
          ),
      },
      displayName: displayNames[RouteFieldName.START_AT],
      displayOrder: 6,
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.VISIBLE_BY_DEFAULT,
      ],
      name: RouteFieldName.START_AT,
    }),
    new DateTimeField({
      displayName: displayNames[RouteFieldName.CREATED_AT],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
      ],
      name: RouteFieldName.CREATED_AT,
    }),
    new DateTimeField({
      displayName: displayNames[RouteFieldName.UPDATED_AT],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
      ],
      name: RouteFieldName.UPDATED_AT,
    }),
    {
      displayName: displayNames[RouteFieldName.QUICK_FILTER],
      features: [
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: RouteFieldName.QUICK_FILTER,
      type: FieldType.STRING,
    },
    // history-only fields, they have artificial valueGetters
    {
      displayName: displayNames[RouteFieldName.STOP_DISPLAY_ORDER],
      features: [
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: RouteFieldName.STOP_DISPLAY_ORDER,
      type: FieldType.NUMBER,
    },
    {
      displayName: displayNames[RouteFieldName.STOP_ALLOTTED_TIME],
      features: [
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: RouteFieldName.STOP_ALLOTTED_TIME,
      type: FieldType.NUMBER,
      valueFormatter: (entity: unknown, value: unknown) => {
        const intl = i18nService.getIntl();
        const allottedTimeSeconds = value as number;

        return Number.isFinite(allottedTimeSeconds)
          ? intl
            ? formatDuration(intl, allottedTimeSeconds / 60)
            : String(Math.round(allottedTimeSeconds / 60))
          : "";
      },
    },
    {
      displayName: displayNames[RouteFieldName.STOP_CHECKED_OFF],
      features: [
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: RouteFieldName.STOP_CHECKED_OFF,
      type: FieldType.BOOLEAN,
      valueFormatter: booleanFormatter,
    },
    {
      csvExportProperties: { valueFormatter: csvDateTimeFormatter },
      displayName: displayNames[RouteFieldName.STOP_SCHEDULED_TIME],
      features: [
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: RouteFieldName.STOP_SCHEDULED_TIME,
      type: FieldType.DATE_TIME,
      valueFormatter: (entity: unknown, date: unknown) =>
        isValidDate(date) ? formatDate(date, "p") : "",
      valueGetter: (entity: unknown, field: IField) => {
        const value = get(entity, field.name);
        if (isNil(value)) {
          return value;
        }

        const date = parseApiDate(value);
        return isValid(date) ? date : undefined;
      },
    },
    // list-view-only fields, computed client-side from the route's checked-off stops
    {
      customGridProperties: {
        cellRenderer: "routeStatusCellRenderer",
      },
      displayName: displayNames[RouteFieldName.STATUS],
      displayOrder: 7,
      features: [
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.VISIBLE_BY_DEFAULT,
      ],
      name: RouteFieldName.STATUS,
      type: FieldType.STRING,
      valueFormatter: (entity: unknown, value: unknown) => {
        const intl = i18nService.getIntl();
        return intl ? getRouteStatusDisplayName(intl, value as RouteStatus) : "";
      },
      valueGetter: (entity: unknown) => getRouteStatus(entity as Route),
    },
    {
      customGridProperties: {
        cellRenderer: "routeProgressCellRenderer",
      },
      displayName: displayNames[RouteFieldName.PROGRESS],
      displayOrder: 8,
      features: [
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.VISIBLE_BY_DEFAULT,
      ],
      name: RouteFieldName.PROGRESS,
      type: FieldType.NUMBER,
      valueFormatter: (entity: unknown, value: unknown) => {
        const intl = i18nService.getIntl();
        const progress = value as number;
        return intl
          ? intl.formatNumber(progress, { maximumFractionDigits: 0, style: "percent" })
          : `${Math.round(progress * 100)}%`;
      },
      valueGetter: (entity: unknown) => getRouteProgress(entity as Route),
    },
    // fake column, to only show the row menu on the List view
    {
      customGridProperties: {
        cellRenderer: "routeActionsCellRenderer",
        // the row-menu icon is self-explanatory and the label does not fit the column,
        // so this header shows no text and names the column with a tooltip instead
        headerComponent: "routeActionsHeader",
        menuTabs: [],
        resizable: false,
        suppressAutoSize: true,
        suppressMenu: true,
        suppressSizeToFit: true,
        tooltipValueGetter: () => "",
      },
      displayName: displayNames[RouteFieldName.ACTIONS],
      displayOrder: 9,
      features: [
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.VISIBLE_BY_DEFAULT,
      ],
      name: RouteFieldName.ACTIONS,
      type: FieldType.OBJECT,
    },
  ].map(createField)
);

export default routeFieldModel;
